// Stints tab (#288) — cross-session stint comparison for a track week.
// One table: every stint across all sessions/drivers at this week, sortable by
// pace / falloff / fuel / stint length. DESCRIPTIVE ONLY (Pit-reversal guardrail):
// falloff is a measured pace-decay slope, never "change the car" advice, and the
// number is GATED to >=5 green laps with no caution — a mid-stint yellow or a
// short stint makes the slope a noise fluke, so those rows show "—" with a note.
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { CarBadge } from "../components/CarBadge";
import { useAuthStore } from "../state/authStore";
import { useRunStore } from "../state/runStore";
import { api } from "../utils/api";
import { carCatalogLabel } from "../utils/carCatalog";
import {
  formatFalloff,
  formatLapTime,
  formatSessionType,
  fuelUnitForDisplay,
  fuelValueForDisplay,
} from "../utils/formatters";

interface Stint {
  id: string;
  runId: string;
  driverName: string | null;
  carId: string | null;
  eventType: string | null;
  setupName: string | null;
  sessionDate: string | null;
  stintIndex: number;
  lapStart: number;
  lapEnd: number;
  totalLaps: number;
  greenLaps: number;
  bestLapTime: number | null;
  avgLapTime: number | null;
  worstLapTime: number | null;
  falloffSPerLap: number | null;
  fuelUsedTotal: number | null;
  fuelUsedGreen: number | null;
  fuelPerLapGreen: number | null;
  hasCaution: boolean;
  endedInPit: boolean;
}
interface StintsResponse {
  trackWeekId: string;
  stints: Stint[];
}

const MIN_FALLOFF_GREEN_LAPS = 5;

/** Falloff only renders when the slope is trustworthy: enough green laps, no yellow. */
function gatedFalloff(s: Stint): number | null {
  return s.greenLaps >= MIN_FALLOFF_GREEN_LAPS && !s.hasCaution ? s.falloffSPerLap : null;
}

type SortCol = "session" | "pace" | "falloff" | "fuel" | "laps";
type SortDir = "asc" | "desc";

function sortValue(s: Stint, col: SortCol): number | null {
  switch (col) {
    case "session": {
      if (!s.sessionDate) return null;
      const t = Date.parse(s.sessionDate);
      return Number.isFinite(t) ? t : null;
    }
    case "pace":
      return s.avgLapTime;
    case "falloff":
      return gatedFalloff(s);
    case "fuel":
      return s.fuelPerLapGreen;
    case "laps":
      return s.greenLaps;
  }
}

export default function StintsPanel() {
  const { trackWeekId: routeWeekId } = useParams<{ trackWeekId?: string }>();
  const { selectedTrackWeekId } = useRunStore();
  const { user } = useAuthStore();
  const weekId = routeWeekId || selectedTrackWeekId || undefined;
  const units = user?.preferences?.units || "standard";

  const [stints, setStints] = useState<Stint[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [sortCol, setSortCol] = useState<SortCol>("session");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  useEffect(() => {
    if (!weekId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<StintsResponse>(`/api/strategy/stints?trackWeekId=${encodeURIComponent(weekId)}`)
      .then((d) => { if (!cancelled) setStints(d.stints); })
      .catch(() => { if (!cancelled) setError("Couldn't load stints."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [weekId, reloadNonce]);

  const onSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      // Fresh column: fastest pace / least falloff / least fuel first; longest
      // stints and newest sessions first.
      setSortDir(col === "session" || col === "laps" ? "desc" : "asc");
    }
  };

  const sorted = useMemo(() => {
    if (!stints) return [];
    const dir = sortDir === "asc" ? 1 : -1;
    // Mirrors the backend order (session_date desc, run_id, stint_index): runs
    // stay grouped when session dates tie (teammates share one timestamp).
    const tiebreak = (a: Stint, b: Stint) =>
      (a.runId < b.runId ? -1 : a.runId > b.runId ? 1 : 0) || a.stintIndex - b.stintIndex;
    return [...stints].sort((a, b) => {
      const va = sortValue(a, sortCol);
      const vb = sortValue(b, sortCol);
      // Rows without a value (incl. gated falloff) always sink to the bottom.
      if (va == null && vb == null) {
        // Stable fallback: newest session first, then run, then stint order.
        const ta = a.sessionDate ? Date.parse(a.sessionDate) : 0;
        const tb = b.sessionDate ? Date.parse(b.sessionDate) : 0;
        return tb - ta || tiebreak(a, b);
      }
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va !== vb) return (va - vb) * dir;
      return tiebreak(a, b);
    });
  }, [stints, sortCol, sortDir]);

  const fuelUnit = fuelUnitForDisplay(units);
  const fmtFuel = (liters: number | null) => {
    const v = fuelValueForDisplay(liters, units);
    return v == null ? "—" : v.toFixed(2);
  };
  const fmtSession = (s: Stint) => {
    const date = s.sessionDate
      ? new Date(s.sessionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : null;
    const type = s.eventType ? formatSessionType(s.eventType) : null;
    return [date, type].filter(Boolean).join(" · ") || "—";
  };

  return (
    <div className="page fade-in">
      <header className="editorial-header">
        <div className="editorial-meta">
          <span className="eyebrow">Telemetry</span>
        </div>
        <h1 className="editorial-title">Stints</h1>
      </header>

      {!weekId && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">Select a track week to see stints.</div>
        </div>
      )}

      {loading && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">Loading stints…</div>
        </div>
      )}

      {error && !loading && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-text">{error}</div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => setReloadNonce((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && stints && stints.length === 0 && (
        <div className="empty-state" style={{ minHeight: "10rem" }}>
          <div className="empty-state-title">No stints yet</div>
          <div className="empty-state-text">
            No stints logged for this week yet. Upload a run to see stint history.
          </div>
        </div>
      )}

      {!loading && !error && sorted.length > 0 && (
        <div className="card" style={st.card}>
          <table className="table">
            <thead>
              <tr>
                <th>Driver</th>
                <SortTh label="Session" col="session" sortCol={sortCol} sortDir={sortDir} onSort={onSort} />
                <th>Car</th>
                <th>Stint</th>
                <SortTh label="Laps" col="laps" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric />
                <SortTh label="Avg pace" col="pace" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric />
                <SortTh label="Falloff" col="falloff" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric />
                <SortTh label={`Green fuel/lap (${fuelUnit})`} col="fuel" sortCol={sortCol} sortDir={sortDir} onSort={onSort} numeric />
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => {
                const falloff = gatedFalloff(s);
                return (
                  <tr key={s.id}>
                    <td>{s.driverName || "—"}</td>
                    <td>
                      {fmtSession(s)}
                      {s.setupName ? <div style={st.setupName}>{s.setupName}</div> : null}
                    </td>
                    <td>
                      {s.carId ? (
                        <span style={st.carCell}>
                          <CarBadge slug={s.carId} size={18} />
                          {carCatalogLabel(s.carId)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span style={{ fontVariantNumeric: "tabular-nums" }}>#{s.stintIndex + 1}</span>
                      {s.endedInPit && <span style={st.pitTag}>pit</span>}
                    </td>
                    <td style={st.num}>
                      <span style={{ color: "var(--flag-green)" }}>{s.greenLaps}g</span>
                      <span style={{ color: "var(--text-secondary)" }}> / {s.totalLaps}</span>
                    </td>
                    <td style={st.num}>{formatLapTime(s.avgLapTime)}</td>
                    <td style={st.num}>
                      {falloff != null ? (
                        formatFalloff(falloff)
                      ) : (
                        <span style={st.gatedCell}>
                          —
                          {s.hasCaution ? (
                            <span
                              style={st.cautionDot}
                              role="img"
                              aria-label="Caution during stint; falloff not shown"
                              title="Caution during stint; falloff not shown"
                            />
                          ) : (
                            <span style={st.gateNote}>&lt; {MIN_FALLOFF_GREEN_LAPS} green laps</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={st.num}>{fmtFuel(s.fuelPerLapGreen)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={st.caption}>
            Falloff is the pace-decay slope over a stint's green laps (s/lap, positive = slowing),
            shown only for stints with {MIN_FALLOFF_GREEN_LAPS}+ green laps and no caution. Measured
            across sessions, not setup advice.
          </div>
        </div>
      )}
    </div>
  );
}

function SortTh({
  label,
  col,
  sortCol,
  sortDir,
  onSort,
  numeric,
}: {
  label: string;
  col: SortCol;
  sortCol: SortCol;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
  numeric?: boolean;
}) {
  const active = col === sortCol;
  return (
    <th
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
      style={numeric ? { textAlign: "right" } : undefined}
    >
      <button type="button" style={st.sortBtn} onClick={() => onSort(col)}>
        {label}
        <span style={{ ...st.sortArrow, opacity: active ? 1 : 0.35 }} aria-hidden="true">
          {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

const st: Record<string, CSSProperties> = {
  card: { padding: "0.5rem 1.5rem 1rem", overflowX: "auto" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  carCell: { display: "inline-flex", alignItems: "center", gap: 8 },
  pitTag: {
    marginLeft: 8,
    fontFamily: "var(--font-body)",
    fontSize: 13,
    color: "var(--text-secondary)",
    border: "1px solid var(--rule-strong)",
    borderRadius: "var(--r-sm)",
    padding: "0 0.35rem",
  },
  gatedCell: { display: "inline-flex", alignItems: "center", gap: 8, color: "var(--text-muted)" },
  gateNote: { fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap" },
  setupName: { fontSize: 13, color: "var(--text-muted)" },
  cautionDot: {
    width: 9,
    height: 9,
    background: "var(--flag-yellow)",
    borderRadius: "var(--r-xs)",
    display: "inline-block",
    flexShrink: 0,
  },
  sortBtn: {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    color: "inherit",
    letterSpacing: "inherit",
    textTransform: "inherit",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  sortArrow: { fontSize: 13, lineHeight: 1 },
  caption: {
    fontFamily: "var(--font-body)",
    fontSize: 13,
    color: "var(--text-secondary)",
    lineHeight: 1.45,
    marginTop: "0.85rem",
    maxWidth: 560,
  },
};
